import { useState, useRef, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import type { DropTargetMonitor } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import Logo from "./assets/images/logo.png";

import Slide1 from "./assets/images/slides/slide1.png";
import Slide2 from "./assets/images/slides/slide2.png";
import Slide3 from "./assets/images/slides/slide3.png";
import Slide4 from "./assets/images/slides/slide4.png";
import Slide5 from "./assets/images/slides/slide5.png";
import Slide6 from "./assets/images/slides/slide6.png";
import Slide7 from "./assets/images/slides/slide7.png";
import Slide8 from "./assets/images/slides/slide8.png";
import Slide9 from "./assets/images/slides/slide9.png";
import Slide10 from "./assets/images/slides/slide10.png";
import Slide11 from "./assets/images/slides/slide11.png";
import Slide12 from "./assets/images/slides/slide12.png";
import Slide13 from "./assets/images/slides/slide13.png";
import Slide14 from "./assets/images/slides/slide14.png";
import Slide15 from "./assets/images/slides/slide15.png";
import Slide16 from "./assets/images/slides/slide16.png";
import Slide17 from "./assets/images/slides/slide17.png";
import Slide18 from "./assets/images/slides/slide18.png";
import Slide19 from "./assets/images/slides/slide19.png";
import Slide20 from "./assets/images/slides/slide20.png";
import Slide21 from "./assets/images/slides/slide21.png";
import Slide22 from "./assets/images/slides/slide22.png";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Slide {
  id: number;
  src: string;
  label: string;
}

interface DragItem {
  index: number;
}

interface SlideCardProps {
  slide: Slide;
  index: number;
  moveSlide: (dragIndex: number, hoverIndex: number) => void;
}

interface StatItem {
  label: string;
  value: string | number;
}

// ─── CONFIG: Replace these with your exported slide image paths/URLs ───────────
const SLIDE_IMAGES: Slide[] = [
  { id: 1, src: Slide1, label: "Slide 1" },
  { id: 2, src: Slide2, label: "Slide 2" },
  { id: 3, src: Slide3, label: "Slide 3" },
  { id: 4, src: Slide4, label: "Slide 4" },
  { id: 5, src: Slide5, label: "Slide 5" },
  { id: 6, src: Slide6, label: "Slide 6" },
  { id: 7, src: Slide7, label: "Slide 7" },
  { id: 8, src: Slide8, label: "Slide 8" },
  { id: 9, src: Slide9, label: "Slide 9" },
  { id: 10, src: Slide10, label: "Slide 10" },
  { id: 11, src: Slide11, label: "Slide 11" },
  { id: 12, src: Slide12, label: "Slide 12" },
  { id: 13, src: Slide13, label: "Slide 13" },
  { id: 14, src: Slide14, label: "Slide 14" },
  { id: 15, src: Slide15, label: "Slide 15" },
  { id: 16, src: Slide16, label: "Slide 16" },
  { id: 17, src: Slide17, label: "Slide 17" },
  { id: 18, src: Slide18, label: "Slide 18" },
  { id: 19, src: Slide19, label: "Slide 19" },
  { id: 20, src: Slide20, label: "Slide 20" },
  { id: 21, src: Slide21, label: "Slide 21" },
  { id: 22, src: Slide22, label: "Slide 22" },
];

// The correct order (by id) — set this to define what "correct" means
const CORRECT_ORDER: number[] = [1, 2, 3, 4, 5, 6];

// ─── Detect touch device for backend switching ────────────────────────────────
const isTouchDevice = (): boolean =>
  "ontouchstart" in window || navigator.maxTouchPoints > 0;

// ─── Shuffle helper ───────────────────────────────────────────────────────────
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Draggable Slide Card ─────────────────────────────────────────────────────
const ITEM_TYPE = "SLIDE";

function SlideCard({ slide, index, moveSlide }: SlideCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: ITEM_TYPE,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: ITEM_TYPE,
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveSlide(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      style={{
        opacity: isDragging ? 0.3 : 1,
        transform: isDragging ? "scale(0.97)" : isOver ? "scale(1.02)" : "scale(1)",
        transition: "transform 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease",
        cursor: isDragging ? "grabbing" : "grab",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: isDragging
          ? "0 2px 8px rgba(0,0,0,0.5)"
          : isOver
            ? "0 0 0 3px #e94560, 0 8px 32px rgba(233,69,96,0.4)"
            : "0 4px 24px rgba(0,0,0,0.5)",
        background: "#0f1623",
        border: isOver ? "2px solid #e94560" : "2px solid rgba(255,255,255,0.08)",
        position: "relative",
        userSelect: "none",
      }}
    >
      {/* Slide number badge */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 10,
          background: "rgba(10,10,20,0.85)",
          backdropFilter: "blur(6px)",
          color: "#e94560",
          fontFamily: "'Courier New', monospace",
          fontWeight: 700,
          fontSize: 13,
          padding: "4px 10px",
          borderRadius: 6,
          border: "1px solid rgba(233,69,96,0.4)",
          letterSpacing: 1,
        }}
      >
        #{index + 1}
      </div>

      {/* Drag handle */}
      <div
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          padding: "6px 8px",
          background: "rgba(10,10,20,0.75)",
          borderRadius: 6,
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: 18,
              height: 2,
              borderRadius: 2,
              background: "rgba(255,255,255,0.5)",
            }}
          />
        ))}
      </div>

      {/* Slide image */}
      <img
        src={slide.src}
        alt={slide.label}
        draggable={false}
        style={{
          width: "100%",
          display: "block",
          aspectRatio: "16 / 9",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />

      {/* Label bar */}
      <div
        style={{
          padding: "10px 14px",
          background: "rgba(10,10,20,0.9)",
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            color: "rgba(255,255,255,0.7)",
            fontFamily: "'Courier New', monospace",
            fontSize: 12,
            letterSpacing: 0.5,
          }}
        >
          {slide.label}
        </span>
        <span style={{ color: "rgba(255,255,255,0.25)", fontSize: 11 }}>
          drag to reorder
        </span>
      </div>
    </div>
  );
}

// ─── Check Order ──────────────────────────────────────────────────────────────
function checkOrder(slides: Slide[]): boolean {
  return slides.every((s, i) => s.id === CORRECT_ORDER[i]);
}

// ─── Score calculation ────────────────────────────────────────────────────────
function calcScore(slides: Slide[]): number {
  let correct = 0;
  slides.forEach((s, i) => {
    if (s.id === CORRECT_ORDER[i]) correct++;
  });
  return Math.round((correct / slides.length) * 100);
}

// ─── Main Game Component ──────────────────────────────────────────────────────
function SlideGame() {
  const [slides, setSlides] = useState<Slide[]>(() => shuffle(SLIDE_IMAGES));
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [score, setScore] = useState<number | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean>(false);
  const [moves, setMoves] = useState<number>(0);

  const moveSlide = useCallback((dragIndex: number, hoverIndex: number): void => {
    setSlides((prev) => {
      const next = [...prev];
      const [removed] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, removed);
      return next;
    });
    setMoves((m) => m + 1);
  }, []);

  const handleSubmit = (): void => {
    const s = calcScore(slides);
    setScore(s);
    setIsCorrect(checkOrder(slides));
    setSubmitted(true);
  };

  const handleReset = (): void => {
    setSlides(shuffle(SLIDE_IMAGES));
    setSubmitted(false);
    setScore(null);
    setIsCorrect(false);
    setMoves(0);
  };

  const stats: StatItem[] = [
    { label: "Slides", value: slides.length },
    { label: "Moves", value: moves },
    { label: "Score", value: submitted && score !== null ? `${score}%` : "—" },
  ];

  const bannerBg = isCorrect
    ? "rgba(99,153,34,0.15)"
    : (score ?? 0) >= 60
      ? "rgba(186,117,23,0.15)"
      : "rgba(233,69,96,0.12)";

  const bannerBorder = isCorrect
    ? "rgba(99,153,34,0.4)"
    : (score ?? 0) >= 60
      ? "rgba(186,117,23,0.4)"
      : "rgba(233,69,96,0.35)";

  const bannerColor = isCorrect
    ? "#97c459"
    : (score ?? 0) >= 60
      ? "#ef9f27"
      : "#e94560";

  return (
    <div
      style={{
        minHeight: "100vh",
        background: ` 
          radial-gradient(ellipse at 15% 10%, #7B3FAF 0%, transparent 55%),
          radial-gradient(ellipse at 85% 85%, #C97FD4 0%, transparent 55%),
          #9B5EC0`,
        fontFamily: "'Courier New', monospace",
        padding: "0 0 60px",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "32px 24px 20px",
          textAlign: "center",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          marginBottom: 28,
          position: "relative",
        }}
      >


        <div
          style={{
            display: "block",
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            padding: "4px 14px",
            borderRadius: 4,
            marginBottom: 30,
          }}
        >
          <img src={Logo} alt="Logo" style={{ width: "20%", verticalAlign: "middle" }} />
        </div>

        <h1
          style={{
            margin: "0 0 8px",
            color: "#ffffff",
            fontSize: "clamp(24px, 5vw, 38px)",
            fontWeight: 700,
            letterSpacing: -0.5,
            fontFamily: "Georgia, serif",
          }}
        >
          Drag and drop to arrange in the correct order
        </h1>


        {/* Stats bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 24,
            marginTop: 20,
          }}
        >
          {stats.map(({ label, value }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{
                  color: "#e94560",
                  fontSize: 30,
                  fontWeight: 700,
                  fontFamily: "Georgia, serif",
                }}
              >
                {value}
              </div>
              <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 20, letterSpacing: 1 }}>
                {label.toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Result Banner */}
      {submitted && (
        <div
          style={{
            margin: "0 20px 24px",
            padding: "16px 20px",
            borderRadius: 12,
            background: bannerBg,
            border: `1px solid ${bannerBorder}`,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 6 }}>
            {isCorrect ? "🎉" : (score ?? 0) >= 60 ? "👍" : "🔄"}
          </div>
          <div
            style={{
              color: bannerColor,
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "Georgia, serif",
              marginBottom: 4,
            }}
          >
            {isCorrect
              ? "Perfect Order!"
              : (score ?? 0) >= 60
                ? `Almost There — ${score}% correct`
                : `Keep Trying — ${score}% correct`}
          </div>
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>
            {isCorrect ? `Completed in ${moves} moves` : "Rearrange and try again"}
          </div>
        </div>
      )}

      {/* Slide List */}
      <div
        style={{
          padding: "0 16px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        {slides.map((slide, index) => (
          <SlideCard
            key={slide.id}
            slide={slide}
            index={index}
            moveSlide={moveSlide}
          />
        ))}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "28px 16px 0",
          maxWidth: 760,
          margin: "0 auto",
        }}
      >
        {!submitted ? (
          <button
            onClick={handleSubmit}
            style={{
              background: "linear-gradient(135deg, #e94560, #c73652)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "16px 0",
              fontSize: 15,
              fontWeight: 700,
              fontFamily: "'Courier New', monospace",
              letterSpacing: 1.5,
              cursor: "pointer",
              textTransform: "uppercase",
              boxShadow: "0 4px 24px rgba(233,69,96,0.35)",
              transition: "transform 0.1s ease, box-shadow 0.1s ease",
            }}
            onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
              (e.currentTarget.style.transform = "scale(0.98)")
            }
            onMouseUp={(e: React.MouseEvent<HTMLButtonElement>) =>
              (e.currentTarget.style.transform = "scale(1)")
            }
          >
            Submit Order
          </button>
        ) : (
          <button
            onClick={handleReset}
            style={{
              background: "transparent",
              color: "rgba(255,255,255,0.75)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 12,
              padding: "14px 0",
              fontSize: 14,
              fontFamily: "'Courier New', monospace",
              letterSpacing: 1,
              cursor: "pointer",
              textTransform: "uppercase",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderColor = "#e94560";
              e.currentTarget.style.color = "#e94560";
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
              e.currentTarget.style.color = "rgba(255,255,255,0.75)";
            }}
          >
            Shuffle & Play Again
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Root export with DnD Provider ───────────────────────────────────────────
export default function App(): JSX.Element {
  const backend = isTouchDevice() ? TouchBackend : HTML5Backend;
  return (
    <DndProvider backend={backend}>
      <SlideGame />
    </DndProvider>
  );
}